import type { FC, ReactElement } from 'react'

/**
 * One stroke weight, one 24-unit box, round joins. Icons are line drawings,
 * never filled glyphs — a filled icon reads louder than the text beside it.
 */

export const ICON_NAMES = [
  'home', 'star', 'grid', 'doc', 'chart', 'box', 'gear', 'bell', 'check',
  'tick', 'clip', 'help', 'down', 'right', 'left', 'filter', 'dl', 'up',
  'search', 'plus', 'dots', 'x', 'info', 'warn', 'note', 'cal', 'users',
  'print', 'trash', 'menu', 'logout', 'updown', 'database', 'inbox', 'layers', 'shield',
] as const

export type IconName = (typeof ICON_NAMES)[number]

const p = (d: string, key?: string): ReactElement => <path key={key} d={d} />

const SHAPES: Record<IconName, ReactElement | ReactElement[]> = {
  home: p('M3.5 10.6 12 3.9l8.5 6.7V19a1.5 1.5 0 0 1-1.5 1.5h-4.2v-6h-5.6v6H5A1.5 1.5 0 0 1 3.5 19z'),
  star: p('m12 3.7 2.6 5.3 5.9.85-4.25 4.15 1 5.85L12 17.1l-5.25 2.75 1-5.85L3.5 9.85l5.9-.85z'),
  grid: [
    <rect key="a" x="3.8" y="3.8" width="6.4" height="6.4" rx="1" />,
    <rect key="b" x="13.8" y="3.8" width="6.4" height="6.4" rx="1" />,
    <rect key="c" x="3.8" y="13.8" width="6.4" height="6.4" rx="1" />,
    <rect key="d" x="13.8" y="13.8" width="6.4" height="6.4" rx="1" />,
  ],
  doc: [
    p('M6.5 3.5h6.6l4.4 4.4V19a1.5 1.5 0 0 1-1.5 1.5H6.5A1.5 1.5 0 0 1 5 19V5a1.5 1.5 0 0 1 1.5-1.5z', 'a'),
    p('M13 3.6V8h4.4', 'b'),
    p('M8.4 13h7.2', 'c'),
    p('M8.4 16.6h4.8', 'd'),
  ],
  chart: [
    p('M4 4v16h16', 'a'),
    p('M8 20v-5.6', 'b'),
    p('M12.5 20V8.4', 'c'),
    p('M17 20v-8.4', 'd'),
  ],
  box: [
    <rect key="a" x="3.5" y="4.2" width="17" height="4.4" rx="1" />,
    p('M5.2 8.6V19a1.5 1.5 0 0 0 1.5 1.5h10.6a1.5 1.5 0 0 0 1.5-1.5V8.6', 'b'),
    p('M10 12.4h4', 'c'),
  ],
  gear: [
    <circle key="a" cx="12" cy="12" r="3.2" />,
    p('M19.1 14.6a1.6 1.6 0 0 0 .32 1.77l.06.06a1.94 1.94 0 1 1-2.75 2.75l-.06-.06a1.6 1.6 0 0 0-1.77-.32 1.6 1.6 0 0 0-.97 1.47v.17a1.94 1.94 0 1 1-3.88 0v-.09a1.6 1.6 0 0 0-1.03-1.46 1.6 1.6 0 0 0-1.77.32l-.06.06A1.94 1.94 0 1 1 4.44 16.5l.06-.06a1.6 1.6 0 0 0 .32-1.77 1.6 1.6 0 0 0-1.47-.97H3.2a1.94 1.94 0 1 1 0-3.88h.09A1.6 1.6 0 0 0 4.75 8.8a1.6 1.6 0 0 0-.32-1.77l-.06-.06A1.94 1.94 0 1 1 7.12 4.2l.06.06a1.6 1.6 0 0 0 1.77.32h.08a1.6 1.6 0 0 0 .97-1.47V3.2a1.94 1.94 0 1 1 3.88 0v.09a1.6 1.6 0 0 0 .97 1.47 1.6 1.6 0 0 0 1.77-.32l.06-.06a1.94 1.94 0 1 1 2.75 2.75l-.06.06a1.6 1.6 0 0 0-.32 1.77v.08a1.6 1.6 0 0 0 1.47.97h.17a1.94 1.94 0 1 1 0 3.88h-.09a1.6 1.6 0 0 0-1.47.97z', 'b'),
  ],
  bell: [
    p('M18 8.6a6 6 0 1 0-12 0c0 6.4-2.6 8.2-2.6 8.2h17.2S18 15 18 8.6', 'a'),
    p('M13.7 20.4a2 2 0 0 1-3.4 0', 'b'),
  ],
  check: [
    <circle key="a" cx="12" cy="12" r="8.4" />,
    p('m8.2 12.3 2.7 2.7 5-5.4', 'b'),
  ],
  tick: p('m4.6 12.6 5 5L19.4 6.8'),
  clip: [
    p('M9.4 3.6h5.2a1 1 0 0 1 1 1v1.6H8.4V4.6a1 1 0 0 1 1-1z', 'a'),
    p('M8.4 6.2H6.5A1.5 1.5 0 0 0 5 7.7V19a1.5 1.5 0 0 0 1.5 1.5h11A1.5 1.5 0 0 0 19 19V7.7a1.5 1.5 0 0 0-1.5-1.5h-1.9', 'b'),
  ],
  help: [
    <circle key="a" cx="12" cy="12" r="8.4" />,
    p('M9.6 9.5a2.5 2.5 0 1 1 3.3 3.1c-.7.3-1 .9-1 1.6v.3', 'b'),
    p('M12 17.2h.01', 'c'),
  ],
  down: p('m6 9.4 6 6 6-6'),
  right: p('m9.4 6 6 6-6 6'),
  left: p('m14.6 6-6 6 6 6'),
  filter: p('M3.8 5h16.4l-6.4 7.6V19l-3.6 1.8v-8.2z'),
  dl: [
    p('M12 3.9v11', 'a'),
    p('m7.5 10.4 4.5 4.5 4.5-4.5', 'b'),
    p('M4.4 20.1h15.2', 'c'),
  ],
  up: p('m6 14.6 6-6 6 6'),
  search: [
    <circle key="a" cx="10.8" cy="10.8" r="6.6" />,
    p('m15.6 15.6 4.6 4.6', 'b'),
  ],
  plus: [p('M12 5v14', 'a'), p('M5 12h14', 'b')],
  dots: [
    <circle key="a" cx="5.2" cy="12" r="1.3" />,
    <circle key="b" cx="12" cy="12" r="1.3" />,
    <circle key="c" cx="18.8" cy="12" r="1.3" />,
  ],
  x: [p('m6 6 12 12', 'a'), p('M18 6 6 18', 'b')],
  info: [
    <circle key="a" cx="12" cy="12" r="8.4" />,
    p('M12 11.2v5.2', 'b'),
    p('M12 7.9h.01', 'c'),
  ],
  warn: [
    p('M12 3.6 22 20.2H2z', 'a'),
    p('M12 9.6v4.4', 'b'),
    p('M12 17.1h.01', 'c'),
  ],
  note: [
    <rect key="a" x="4.2" y="3.8" width="15.6" height="16.4" rx="1.4" />,
    p('M7.8 8.6h8.4', 'b'),
    p('M7.8 12.4h8.4', 'c'),
    p('M7.8 16.2h5', 'd'),
  ],
  cal: [
    <rect key="a" x="3.5" y="5.2" width="17" height="15.3" rx="1.4" />,
    p('M3.5 10h17', 'b'),
    p('M8 3.5v3.4', 'c'),
    p('M16 3.5v3.4', 'd'),
  ],
  users: [
    p('M15.8 20.4v-1.7a3.9 3.9 0 0 0-3.9-3.9H7.4a3.9 3.9 0 0 0-3.9 3.9v1.7', 'a'),
    <circle key="b" cx="9.65" cy="7.5" r="3.4" />,
    p('M17.2 4.3a3.4 3.4 0 0 1 0 6.5', 'c'),
    p('M20.5 20.4v-1.7a3.9 3.9 0 0 0-2.9-3.76', 'd'),
  ],
  print: [
    p('M7 9.4V3.7h10v5.7', 'a'),
    p('M7 17.4H5.2a1.5 1.5 0 0 1-1.5-1.5v-4.9a1.5 1.5 0 0 1 1.5-1.5h13.6a1.5 1.5 0 0 1 1.5 1.5v4.9a1.5 1.5 0 0 1-1.5 1.5H17', 'b'),
    <rect key="c" x="7" y="14.2" width="10" height="6.2" rx="1" />,
  ],
  trash: [
    p('M4.4 6.9h15.2', 'a'),
    p('M9.6 6.9V4.4a.9.9 0 0 1 .9-.9h3a.9.9 0 0 1 .9.9v2.5', 'b'),
    p('m6.4 6.9 1 12.6a1.5 1.5 0 0 0 1.5 1.4h6.2a1.5 1.5 0 0 0 1.5-1.4l1-12.6', 'c'),
    p('M10.5 10.7v6', 'd'),
    p('M13.5 10.7v6', 'e'),
  ],
  menu: [p('M4 6.5h16', 'a'), p('M4 12h16', 'b'), p('M4 17.5h16', 'c')],
  logout: [
    p('M9.5 20.5H6A1.5 1.5 0 0 1 4.5 19V5A1.5 1.5 0 0 1 6 3.5h3.5', 'a'),
    p('m15.5 16.5 4.5-4.5-4.5-4.5', 'b'),
    p('M20 12H9.5', 'c'),
  ],
  updown: [p('m7.5 9 4.5-4.5L16.5 9', 'a'), p('m7.5 15 4.5 4.5 4.5-4.5', 'b')],
  database: [
    <ellipse key="a" cx="12" cy="5.6" rx="7" ry="2.6" />,
    p('M5 5.6v12.8c0 1.43 3.13 2.6 7 2.6s7-1.17 7-2.6V5.6', 'b'),
    p('M5 12c0 1.43 3.13 2.6 7 2.6s7-1.17 7-2.6', 'c'),
  ],
  inbox: [
    p('M3.5 13h4.8l1.5 3h4.4l1.5-3h4.8', 'a'),
    p('M6 5h12l2.5 8v5.5A1.5 1.5 0 0 1 19 20H5a1.5 1.5 0 0 1-1.5-1.5V13z', 'b'),
  ],
  layers: [p('m12 3.5 8.5 4.7L12 13 3.5 8.2z', 'a'), p('m3.5 12.6 8.5 4.8 8.5-4.8', 'b'), p('m3.5 16.6 8.5 4.8 8.5-4.8', 'c')],
  shield: p('M12 3.4 4.8 6.2v5.6c0 4.4 3.1 7.7 7.2 8.8 4.1-1.1 7.2-4.4 7.2-8.8V6.2z'),
}

export interface IconProps {
  name: IconName
  size?: number
  colour?: string
}

export const Icon: FC<IconProps> = ({ name, size = 20, colour = 'currentColor' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={colour}
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    style={{ display: 'block', flex: 'none' }}
  >
    {SHAPES[name]}
  </svg>
)
